import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, AlertTriangle, LogOut, Wallet, TrendingUp, Shield, PieChart as PieIcon, Target, Download, Sparkles, Trophy, Flame, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, authedFetch } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { emptyProfile, fmtINR, summarize } from "@/lib/financial";
import { tierMeta, fireConfetti } from "@/lib/gamification";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import ClientSidebar from "@/components/ClientSidebar";

const PALETTE = ["#1A3626", "#8B9D8B", "#C78D4A", "#4A6E82", "#9E473D", "#3A5C45", "#C97B2A", "#A7C7E7"];

function Stat({ label, value, sub, accent }) {
  return (
    <div className="rounded-2xl border border-[#E5E2DA] bg-white p-7 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">{label}</div>
      <div className={`mono text-3xl font-medium mt-3 ${accent || "text-[#1C1D1C]"}`}>{value}</div>
      {sub && <div className="text-sm text-[#8A8D8A] mt-2">{sub}</div>}
    </div>
  );
}

export default function ClientDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gam, setGam] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    let mounted = true;
    (async () => {
      try {
        const [pRes, gRes] = await Promise.all([
          api.get("/profile"),
          api.get("/gamification").catch(() => ({ data: null })),
        ]);
        if (!mounted) return;
        setProfile({ ...emptyProfile, ...pRes.data });
        setGam(gRes.data);
      } catch (err) {
        console.error("dashboard fetch failed", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user, authLoading, navigate]);

  // Celebrate when tier or unlocked count improves since last visit
  useEffect(() => {
    if (!gam || !user) return;
    try {
      const key = `wah_gam_${user.email || "u"}`;
      const prev = JSON.parse(localStorage.getItem(key) || "{}");
      const tierChanged = prev.tierKey && prev.tierKey !== gam.tier.key;
      const badgesGained = typeof prev.unlocked === "number" && gam.unlocked_count > prev.unlocked;
      if (tierChanged || badgesGained) {
        fireConfetti();
        const msg = tierChanged
          ? `You leveled up to ${gam.tier.label}!`
          : `New badge${gam.unlocked_count - (prev.unlocked || 0) > 1 ? "s" : ""} unlocked!`;
        toast.success(msg, { duration: 4500 });
      }
      localStorage.setItem(key, JSON.stringify({ tierKey: gam.tier.key, unlocked: gam.unlocked_count }));
    } catch (err) {
      console.warn("gamification celebration skipped", err);
    }
  }, [gam, user]);

  const s = useMemo(() => summarize(profile || emptyProfile), [profile]);

  const assetData = useMemo(() => [
    ...Object.entries(profile?.financial_assets || {}),
    ...Object.entries(profile?.fixed_assets || {}),
  ].map(([k, v]) => ({ key: k, name: k.replace(/_/g, " "), value: Number(v || 0) })).filter((d) => d.value > 0), [profile?.financial_assets, profile?.fixed_assets]);

  const emiData = useMemo(() => (profile?.liabilities_table || [])
    .map((l) => ({ name: (l.type || "Loan").replace(" Loan", ""), value: Number(l.monthly_emi || 0) }))
    .filter((d) => d.value > 0), [profile?.liabilities_table]);

  if (authLoading || loading) return <div className="min-h-screen flex items-center justify-center text-[#5B5E5B]">Loading…</div>;

  const submitted = profile?.submitted;

  const emiBadge = s.emiRatio > 50 ? { label: "High", color: "text-[#9E473D]", bg: "bg-[#9E473D]/10" } :
                  s.emiRatio > 30 ? { label: "Moderate", color: "text-[#C78D4A]", bg: "bg-[#C78D4A]/10" } :
                                    { label: "Healthy", color: "text-[#3A5C45]", bg: "bg-[#3A5C45]/10" };

  const goalsCount = (profile?.goals || []).length;

  return (
    <div className="min-h-screen page-tinted-soft lg:flex">
      <ClientSidebar user={user} onLogout={async () => { await logout(); navigate("/"); }}/>
      <div className="flex-1">

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 fade-up">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Your snapshot</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight mt-3">
              {submitted ? "Here's where you stand" : "Let's build your profile"}
            </h1>
            <p className="text-[#5B5E5B] mt-3 max-w-xl">
              {submitted
                ? "Your advisor has the full picture. Below are the headline numbers from what you shared."
                : "Complete the intake to see your net worth, EMI ratio and protection gap at a glance."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${submitted ? "bg-[#3A5C45]/10 text-[#3A5C45]" : "bg-[#C78D4A]/10 text-[#C78D4A]"}`}>
              {submitted ? <CheckCircle2 className="h-4 w-4"/> : <AlertTriangle className="h-4 w-4"/>}
              <span className="text-sm font-medium">{submitted ? "Submitted" : "Draft"}</span>
            </div>
            <Button data-testid="download-pdf" variant="outline" onClick={async () => {
              try {
                const res = await authedFetch("/profile/snapshot.pdf");
                if (!res.ok) throw new Error("download failed");
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `WAH_Snapshot_${(user?.name || "client").replace(/ /g,"_")}.pdf`; a.click();
                URL.revokeObjectURL(url);
                toast.success("Snapshot downloaded");
              } catch (err) {
                console.error("pdf download failed", err);
                toast.error("Couldn't download PDF");
              }
            }} className="rounded-full border-[#E5E2DA]">
              <Download className="h-4 w-4 mr-2"/> PDF
            </Button>
            <Button data-testid="open-intake" onClick={() => navigate("/intake")} className="rounded-full bg-[#1A3626] hover:bg-[#2C4C3B] text-white">
              {submitted ? "Edit details" : "Continue intake"} <ArrowRight className="h-4 w-4 ml-1"/>
            </Button>
          </div>
        </div>

        {gam && (
          <div className="mb-8">
            <div className="rounded-3xl border border-[#E5E2DA] bg-white p-6 md:p-8 grid md:grid-cols-[auto_1fr_auto] gap-6 md:gap-10 items-center fade-up" data-testid="score-hero">
              {/* Ring */}
              {(() => {
                const meta = tierMeta(gam.tier.key);
                const pct = Math.min(100, Math.max(0, gam.score));
                const size = 128;
                const stroke = 10;
                const r = (size - stroke) / 2;
                const c = 2 * Math.PI * r;
                const off = c - (pct / 100) * c;
                return (
                  <div className="relative flex items-center justify-center mx-auto md:mx-0" style={{ width: size, height: size }}>
                    <svg width={size} height={size} className="-rotate-90">
                      <circle cx={size / 2} cy={size / 2} r={r} stroke="#E5E2DA" strokeWidth={stroke} fill="none" />
                      <circle cx={size / 2} cy={size / 2} r={r} stroke={meta.color} strokeWidth={stroke} fill="none"
                        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
                        style={{ transition: "stroke-dashoffset 1.2s ease-out" }} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="mono text-3xl font-medium text-[#1C1D1C]" data-testid="score-value">{gam.score}</div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#8A8D8A] mt-0.5">/ 100</div>
                    </div>
                  </div>
                );
              })()}

              {/* Tier + progress */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: tierMeta(gam.tier.key).color }}>
                  Wealth tier
                </div>
                <div className="font-heading text-2xl md:text-3xl mt-1" style={{ color: tierMeta(gam.tier.key).color }}>
                  {gam.tier.label}
                </div>
                {gam.tier.next ? (
                  <p className="text-sm text-[#5B5E5B] mt-1.5">
                    <span className="mono">{gam.tier.points_to_next}</span> point{gam.tier.points_to_next === 1 ? "" : "s"} away from <span className="font-medium">{gam.tier.next.label}</span>
                  </p>
                ) : (
                  <p className="text-sm text-[#5B5E5B] mt-1.5">Top tier reached — keep the discipline going.</p>
                )}
              </div>

              {/* Streak + badges */}
              <div className="flex md:flex-col gap-3 md:gap-2 md:items-end">
                <div className="flex items-center gap-2 rounded-full bg-[#C78D4A]/10 text-[#C97B2A] px-4 py-2">
                  <Flame className="h-4 w-4" />
                  <span className="text-sm font-medium mono">{gam.streak_months}</span>
                  <span className="text-xs">month streak</span>
                </div>
                <button
                  data-testid="view-achievements"
                  onClick={() => navigate("/achievements")}
                  className="flex items-center gap-2 rounded-full border border-[#E5E2DA] hover:bg-[#F4F2EC] px-4 py-2 transition-colors">
                  <Trophy className="h-4 w-4 text-[#C97B2A]" />
                  <span className="text-sm font-medium mono">{gam.unlocked_count}/{gam.total_badges}</span>
                  <span className="text-xs text-[#5B5E5B]">badges</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
          <div className="md:col-span-4 lg:col-span-4">
            <Stat label="Net worth" value={`₹ ${fmtINR(s.netWorth)}`} sub={`${fmtINR(s.totalAssets)} − ${fmtINR(s.totalLiab)}`} accent={s.netWorth >= 0 ? "text-[#1A3626]" : "text-[#9E473D]"}/>
          </div>
          <div className="md:col-span-4 lg:col-span-4">
            <Stat label="Monthly inflow" value={`₹ ${fmtINR(s.monthlyInflow)}`} sub={`Yearly extras ₹ ${fmtINR(s.yearlyInflow)}`}/>
          </div>
          <div className="md:col-span-4 lg:col-span-4">
            <Stat label="EMI / income" value={`${s.emiRatio.toFixed(1)}%`} sub={`₹ ${fmtINR(s.totalEmi)} every month`} accent={emiBadge.color}/>
          </div>
          <div className="md:col-span-4 lg:col-span-4">
            <Stat label="Monthly expenses" value={`₹ ${fmtINR(s.monthlyExpense)}`} sub={`Annual ₹ ${fmtINR(s.annualExpense)}`}/>
          </div>
          <div className="md:col-span-4 lg:col-span-4">
            <Stat label="Monthly SIP" value={`₹ ${fmtINR(s.monthlySip)}`} sub={s.monthlyInflow ? `${((s.monthlySip/s.monthlyInflow)*100).toFixed(1)}% of inflow` : "—"}/>
          </div>
          <div className="md:col-span-4 lg:col-span-4">
            <Stat label="Savings rate" value={`${s.savingsRate.toFixed(1)}%`} sub="Income left after expenses & EMIs" accent={s.savingsRate > 20 ? "text-[#3A5C45]" : "text-[#C78D4A]"}/>
          </div>

          <div className="md:col-span-4 lg:col-span-7 rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Asset allocation</div>
                <div className="font-heading text-xl mt-2">How your wealth is spread</div>
              </div>
              <PieIcon className="h-5 w-5 text-[#8B9D8B]"/>
            </div>
            {assetData.length === 0 ? (
              <div className="text-[#8A8D8A] py-12 text-center text-sm">No assets recorded yet.</div>
            ) : (
              <div className="grid sm:grid-cols-2 items-center gap-6">
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={assetData} dataKey="value" innerRadius={60} outerRadius={100} paddingAngle={2}>
                        {assetData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]}/>)}
                      </Pie>
                      <Tooltip formatter={(v) => `₹ ${fmtINR(v)}`}/>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5 max-h-[260px] overflow-auto pr-2">
                  {assetData.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }}/>
                        <span className="capitalize text-[#5B5E5B]">{a.name}</span>
                      </div>
                      <span className="mono text-[#1C1D1C]">₹ {fmtINR(a.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="md:col-span-4 lg:col-span-5 rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-[#1A3626]"/>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Protection</div>
            </div>
            <div className="font-heading text-xl">Where your safety net stands</div>
            <div className="mt-5 space-y-4">
              <div className="rounded-xl bg-[#F4F2EC] p-4">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Term life</div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${profile?.insurance?.has_term_insurance ? "bg-[#3A5C45]/15 text-[#3A5C45]" : "bg-[#9E473D]/10 text-[#9E473D]"}`}>
                    {profile?.insurance?.has_term_insurance ? "Active" : "Missing"}
                  </span>
                </div>
                <div className="mono text-sm text-[#5B5E5B] mt-2">Cover ₹ {fmtINR(profile?.insurance?.term_cover_amount)} · Suggested ₹ {fmtINR(s.termTarget)}</div>
                {s.termGap > 0 && profile?.insurance?.has_term_insurance && (
                  <div className="text-xs text-[#C78D4A] mt-1">Gap of ₹ {fmtINR(s.termGap)} vs. 15× annual income.</div>
                )}
              </div>
              <div className="rounded-xl bg-[#F4F2EC] p-4">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Health</div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${profile?.insurance?.has_health_insurance ? "bg-[#3A5C45]/15 text-[#3A5C45]" : "bg-[#9E473D]/10 text-[#9E473D]"}`}>
                    {profile?.insurance?.has_health_insurance ? "Active" : "Missing"}
                  </span>
                </div>
                <div className="mono text-sm text-[#5B5E5B] mt-2">Cover ₹ {fmtINR(profile?.insurance?.health_cover_amount)}</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-4 lg:col-span-5 rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-[#1A3626]"/>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Investing rhythm</div>
            </div>
            <div className="font-heading text-xl">SIPs & top-ups</div>
            <div className="mono text-3xl mt-4">₹ {fmtINR(s.monthlySip)}<span className="text-base text-[#8A8D8A] ml-1">/mo</span></div>
            <div className="mono text-sm text-[#5B5E5B] mt-2">+ ₹ {fmtINR(s.yearlyInv)} annually</div>
          </div>

          <div className="md:col-span-4 lg:col-span-7 rounded-2xl border border-[#E5E2DA] bg-white p-7">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="h-5 w-5 text-[#1A3626]"/>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">EMI breakdown</div>
            </div>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-xl">Monthly commitments</div>
              <span className={`text-xs px-2.5 py-1 rounded-full ${emiBadge.bg} ${emiBadge.color}`}>{emiBadge.label}</span>
            </div>
            {emiData.length === 0 ? (
              <div className="text-[#8A8D8A] py-8 text-center text-sm">No EMIs recorded.</div>
            ) : (
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={emiData}>
                    <XAxis dataKey="name" tick={{ fill: "#5B5E5B", fontSize: 12 }} axisLine={false} tickLine={false}/>
                    <YAxis tick={{ fill: "#8A8D8A", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${Math.round(v/1000)}k`}/>
                    <Tooltip formatter={(v) => `₹ ${fmtINR(v)}`} cursor={{ fill: "#F4F2EC" }}/>
                    <Bar dataKey="value" fill="#1A3626" radius={[8,8,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {goalsCount > 0 && (
            <div className="md:col-span-8 lg:col-span-12 rounded-2xl border border-[#E5E2DA] bg-white p-7">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-5 w-5 text-[#1A3626]"/>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Your goals</div>
              </div>
              <div className="font-heading text-xl mb-4">{goalsCount} goal{goalsCount > 1 ? "s" : ""} on your radar</div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {(profile?.goals || []).map((g, i) => {
                  const gp = (gam?.goals_progress || [])[i];
                  const pct = gp ? Math.min(100, gp.progress_pct) : 0;
                  return (
                    <div key={g._id || `goal-${g.name}-${i}`} className="rounded-xl border border-[#E5E2DA] p-4 bg-[#FDFBF7]">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">{g.name}</div>
                        {gp && <div className="mono text-xs text-[#1A3626]">{pct.toFixed(0)}%</div>}
                      </div>
                      <div className="mono text-lg text-[#1A3626] mt-1">₹ {fmtINR(g.today_cost)}</div>
                      <div className="text-xs text-[#8A8D8A]">Target {g.target_year || "—"} · {g.inflation || 7}% infl.</div>
                      {gp && (
                        <div className="mt-3 h-1.5 bg-[#E5E2DA] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700 bg-[#1A3626]" style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {submitted && profile?.submission_analysis && (
            <div className="md:col-span-8 lg:col-span-12 rounded-2xl border border-[#E5E2DA] bg-white p-7 md:p-9">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-[#C97B2A]"/>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C97B2A]">Your personalised review</div>
              </div>
              <div className="font-heading text-2xl mb-5">Where you stand & what to do next</div>
              {(profile.submission_analysis.flags || []).length > 0 && (
                <div className="grid sm:grid-cols-2 gap-3 mb-7">
                  {profile.submission_analysis.flags.map((f, i) => {
                    const palette = f.severity === "high" ? { bg: "bg-[#9E473D]/10", text: "text-[#9E473D]" }
                      : f.severity === "warn" ? { bg: "bg-[#C78D4A]/10", text: "text-[#C97B2A]" }
                      : { bg: "bg-[#3A5C45]/10", text: "text-[#3A5C45]" };
                    return (
                      <div key={`flag-${f.title}-${i}`} className="rounded-xl border border-[#E5E2DA] p-4 bg-[#FDFBF7]">
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${palette.bg} ${palette.text}`}>{f.severity}</span>
                        <div className="font-medium mt-2">{f.title}</div>
                        <div className="text-sm text-[#5B5E5B] mt-1 leading-relaxed">{f.body}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              {profile.submission_analysis.ai_text && (
                <div className="space-y-3 max-w-3xl">
                  {profile.submission_analysis.ai_text.split("\n\n").filter(Boolean).map((para, i) => (
                    <p key={`para-${i}`} className="text-[#1C1D1C] leading-relaxed">{para}</p>
                  ))}
                </div>
              )}
              <div className="text-xs text-[#8A8D8A] mt-5">A full PDF report has also been sent to your email.</div>
            </div>
          )}
        </div>

        {!submitted && (
          <div className="mt-10 rounded-2xl border border-[#E5E2DA] bg-[#1A3626] text-[#FDFBF7] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C78D4A]">Almost there</div>
              <h3 className="font-heading text-2xl mt-2">Submit your intake so your advisor can plan.</h3>
            </div>
            <Button data-testid="cta-finish-intake" onClick={() => navigate("/intake")} className="rounded-full bg-[#FDFBF7] text-[#1A3626] hover:bg-white h-12 px-6">
              Continue <ArrowRight className="h-4 w-4 ml-1"/>
            </Button>
          </div>
        )}
      </main>
      </div>
    </div>
  );
}
